import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";

/**
 * Workshop: Appearance section for Settings.
 * Theme selector with Dark / Light / System options.
 */

type Theme = "dark" | "light" | "system";

interface ThemeOption {
  id: Theme;
  label: string;
  icon: React.ReactNode;
  testId: string;
}

const THEME_OPTIONS: ThemeOption[] = [
  {
    id: "dark",
    label: "Dark",
    icon: <Icons.Moon className="h-5 w-5" />,
    testId: "appearance-theme-dark",
  },
  {
    id: "light",
    label: "Light",
    icon: <Icons.Sun className="h-5 w-5" />,
    testId: "appearance-theme-light",
  },
  {
    id: "system",
    label: "System",
    icon: <Icons.Smartphone className="h-5 w-5" />,
    testId: "appearance-theme-system",
  },
];

interface AppearanceSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function AppearanceSection({
  className,
  "data-testid": testId = "settings-appearance",
}: AppearanceSectionProps) {
  const [activeTheme, setActiveTheme] = useState<Theme>("dark");

  return (
    <div className={cn("flex gap-2", className)} data-testid={testId}>
      {THEME_OPTIONS.map((option) => {
        const isActive = activeTheme === option.id;
        return (
          <button
            key={option.id}
            type="button"
            className={cn(
              "flex flex-1 flex-col items-center gap-2 rounded-lg border p-4 transition-colors",
              isActive
                ? "border-accent bg-accent/10 text-accent"
                : "border-glass-stroke-subtle text-secondary hover:bg-btn-ghost-fill",
            )}
            onClick={() => setActiveTheme(option.id)}
            data-testid={option.testId}
          >
            {option.icon}
            <span className="text-[11px] font-medium">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export type { AppearanceSectionProps };
